import { useEffect, useState, type FormEvent } from "react";
import type { AppController, AppSnapshot, CompleteCheckInInput, ExecuteAction } from "../types";

interface CheckInViewProps {
  snapshot: AppSnapshot;
  controller: AppController;
  execute: ExecuteAction;
  onBackfillFood: () => void;
  onBackfillWeight: () => void;
}

export function CheckInView({
  snapshot,
  controller,
  execute,
  onBackfillFood,
  onBackfillWeight,
}: CheckInViewProps) {
  const checkIn = snapshot.checkIn;
  const [step, setStep] = useState(1);
  const [loggedEverything, setLoggedEverything] = useState<CompleteCheckInInput["loggedEverything"] | "">("");
  const [compliance, setCompliance] = useState(100);
  const [notes, setNotes] = useState("");
  const [acceptedBaseline, setAcceptedBaseline] = useState(checkIn.recommendedBaselineCalories);
  const [acceptedProtein, setAcceptedProtein] = useState(checkIn.proposedProteinGrams);
  const [previewing, setPreviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => setAcceptedBaseline(checkIn.recommendedBaselineCalories), [checkIn.recommendedBaselineCalories]);
  useEffect(() => setAcceptedProtein(checkIn.proposedProteinGrams), [checkIn.proposedProteinGrams]);

  const acceptedProteinFloor = calorieFloorForProtein(
    acceptedProtein,
    snapshot.goals.fatShareOfRemainder,
    checkIn.dailyFloorPolicy,
  );

  if (checkIn.status === "not-due") {
    return (
      <section className="page-section" aria-labelledby="check-in-heading">
        <header className="page-heading"><div><p className="eyebrow">Weekly review</p><h1 id="check-in-heading">Check-in</h1></div></header>
        <article className="card check-in-not-due">
          <div className="success-mark" aria-hidden="true">✓</div>
          <h2>You’re up to date</h2>
          <p>Your next check-in is {formatLongDate(checkIn.dueDate)}. Until then, keep logging as consistently as you can.</p>
          <dl className="definition-list">
            <div><dt>Current baseline</dt><dd>{snapshot.week.baselineDailyCalories.toLocaleString()} kcal/day</dd></div>
            <div><dt>Daily protein</dt><dd>{snapshot.week.fixedDailyProteinGrams.toFixed(0)} g</dd></div>
            <div><dt>Check-in day</dt><dd>{checkIn.nominatedDayLabel}</dd></div>
          </dl>
        </article>
      </section>
    );
  }

  async function complete(event: FormEvent) {
    event.preventDefault();
    if (!loggedEverything) return;
    setSubmitting(true);
    await execute(
      () => controller.completeCheckIn({
        loggedEverything,
        calorieCompliancePercent: compliance,
        notes: notes.trim(),
        acceptedBaselineCalories: Math.round(acceptedBaseline),
        acceptedProteinGrams: Math.round(acceptedProtein),
      }),
      { successMessage: "Check-in complete. Your new week is ready." },
    );
    setSubmitting(false);
  }

  async function reviewTarget() {
    if (!loggedEverything) return;
    setPreviewing(true);
    const result = await execute(
      () => controller.previewCheckIn({
        loggedEverything,
        calorieCompliancePercent: compliance,
        notes: notes.trim(),
      }),
      { errorMessage: "The recommendation could not be prepared. Review your entries and try again." },
    );
    setPreviewing(false);
    if (result.ok) setStep(3);
  }

  return (
    <section className="page-section check-in-page" aria-labelledby="check-in-heading">
      <header className="page-heading">
        <div><p className="eyebrow">Weekly review</p><h1 id="check-in-heading">Check-in</h1></div>
        {checkIn.status === "overdue" && <span className="status-pill status-pill--warning">Overdue</span>}
      </header>

      <ol className="stepper" aria-label="Check-in progress">
        {["Review", "Reflect", "Plan"].map((label, index) => {
          const stepNumber = index + 1;
          return (
            <li key={label} className={stepNumber === step ? "stepper__item stepper__item--current" : stepNumber < step ? "stepper__item stepper__item--done" : "stepper__item"}>
              <span>{stepNumber < step ? "✓" : stepNumber}</span><small>{label}</small>
            </li>
          );
        })}
      </ol>

      {step === 1 && (
        <div className="stack">
          <article className="card">
            <div className="card__heading"><div><p className="eyebrow">Step 1 of 3</p><h2>Review your week</h2></div></div>
            <div className="metric-grid">
              <div className="metric-tile"><span>5-day trend</span><strong>{checkIn.trendWeight === undefined ? "—" : `${checkIn.trendWeight.toFixed(1)} ${snapshot.weight.unit}`}</strong><small>{checkIn.trendWeightSampleSize === 0 ? "No usable weights" : `${checkIn.trendWeightSampleSize} weight${checkIn.trendWeightSampleSize === 1 ? "" : "s"} used`}</small></div>
              <div className="metric-tile"><span>Measured pace</span><strong>{checkIn.measuredWeeklyChangePercent === undefined ? "—" : `${signed(checkIn.measuredWeeklyChangePercent)}%`}</strong><small>per week</small></div>
              <div className="metric-tile"><span>Planned pace</span><strong>{checkIn.expectedWeeklyChangePercent === undefined ? "Maintain" : `${signed(checkIn.expectedWeeklyChangePercent)}%`}</strong><small>per week</small></div>
              <div className="metric-tile"><span>Previous baseline</span><strong>{checkIn.previousBaselineCalories.toLocaleString()}</strong><small>kcal/day</small></div>
            </div>
            {checkIn.noWeightData && (
              <div className="notice notice--info"><div><strong>No calorie change recommended</strong><p>There is not enough weight data to assess progress, so the recommendation keeps your previous baseline.</p></div></div>
            )}
          </article>

          <article className="card">
            <div className="card__heading"><div><h2>Anything missing?</h2><p>Backfill the closing week before completing this check-in.</p></div></div>
            {checkIn.allowedBackfillDates.length > 0 ? (
              <div className="button-row">
                <button className="button button--secondary" type="button" onClick={onBackfillFood}>Backfill food</button>
                <button className="button button--secondary" type="button" onClick={onBackfillWeight}>Backfill weight</button>
              </div>
            ) : <p className="supporting-text">There are no eligible backfill dates in this closing week.</p>}
          </article>

          <button className="button button--primary button--full" type="button" onClick={() => setStep(2)}>Continue</button>
        </div>
      )}

      {step === 2 && (
        <article className="card">
          <div className="card__heading"><div><p className="eyebrow">Step 2 of 3</p><h2>Reflect on your logging</h2><p>Honest answers help put the scale trend in context.</p></div></div>
          <fieldset className="fieldset questionnaire-fieldset">
            <legend>Did you log everything you ate and drank?</legend>
            <div className="choice-grid">
              {(["yes", "no", "unsure"] as const).map((answer) => (
                <label className={`choice-card ${loggedEverything === answer ? "choice-card--selected" : ""}`} key={answer}>
                  <input type="radio" name="complete-logging" value={answer} checked={loggedEverything === answer} onChange={() => setLoggedEverything(answer)} />
                  <span>{answer === "yes" ? "Yes" : answer === "no" ? "No" : "Not sure"}</span>
                </label>
              ))}
            </div>
          </fieldset>
          <label className="range-field">
            <span><strong>How closely did you follow your calorie allocations?</strong><output>{compliance}%</output></span>
            <input type="range" min="0" max="100" step="5" value={compliance} onChange={(event) => setCompliance(Number(event.target.value))} />
            <small><span>Not closely</span><span>Exactly</span></small>
          </label>
          <label className="field"><span>Anything else? <em>optional</em></span><textarea rows={3} value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Travel, illness, unusual meals…" /></label>
          <div className="button-row button-row--split">
            <button className="button button--ghost" type="button" onClick={() => setStep(1)}>Back</button>
            <button className="button button--primary" type="button" disabled={!loggedEverything || previewing} onClick={reviewTarget}>{previewing ? "Preparing…" : "Review target"}</button>
          </div>
        </article>
      )}

      {step === 3 && (
        <form className="stack" onSubmit={complete}>
          <article className="card recommendation-card">
            <div className="card__heading"><div><p className="eyebrow">Step 3 of 3</p><h2>Set next week’s baseline</h2></div></div>
            <div className="recommendation">
              <span>Recommended</span>
              <strong>{checkIn.recommendedBaselineCalories.toLocaleString()} kcal/day</strong>
              <small>Calculated from your goal, trend and compliance answers</small>
            </div>
            <label className="field target-field">
              <span>Your baseline for next week</span>
              <span className="input-with-suffix input-with-suffix--large"><input type="number" inputMode="numeric" min={acceptedProteinFloor} max="50000" step="1" required value={acceptedBaseline} onChange={(event) => setAcceptedBaseline(Number(event.target.value))} /><i>kcal/day</i></span>
            </label>
            <label className="field target-field">
              <span>Your daily protein target</span>
              <span className="input-with-suffix input-with-suffix--large"><input type="number" inputMode="numeric" min="1" max="1000" step="1" required value={acceptedProtein} onChange={(event) => setAcceptedProtein(Number(event.target.value))} /><i>g/day</i></span>
            </label>
            <p className="field-hint">The app proposes {checkIn.proposedProteinGrams.toFixed(0)} g from your check-in trend and goal. You can override it; this changes the daily calorie floor to {acceptedProteinFloor.toLocaleString()} kcal.</p>
            <p className="field-hint">You remain in control. The guideline-bounded recommendation range is {checkIn.minimumRecommendedCalories.toLocaleString()}–{checkIn.maximumRecommendedCalories.toLocaleString()} kcal/day; the daily safety floor still applies.</p>
          </article>

          <article className="card">
            <div className="card__heading"><h2>Next week at a glance</h2></div>
            <dl className="definition-list">
              <div><dt>Weekly budget</dt><dd>{Math.round(acceptedBaseline * 7).toLocaleString()} kcal</dd></div>
              <div><dt>Protein each day</dt><dd>{Math.round(acceptedProtein)} g</dd></div>
              <div><dt>Protein rule</dt><dd>{proteinRule(snapshot.goals.kind)}</dd></div>
              <div><dt>Check-in answers</dt><dd>{compliance}% compliance · logging {loggedEverything}</dd></div>
            </dl>
            <p className="supporting-text">Daily allocations reset equally after check-in. You can redistribute them again from the Week screen.</p>
          </article>

          <div className="button-row button-row--split">
            <button className="button button--ghost" type="button" onClick={() => setStep(2)}>Back</button>
            <button className="button button--primary" type="submit" disabled={submitting}>{submitting ? "Completing…" : "Complete check-in"}</button>
          </div>
        </form>
      )}
    </section>
  );
}

function formatLongDate(date: string) {
  return new Date(`${date}T12:00:00`).toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" });
}

function signed(value: number) {
  return `${value > 0 ? "+" : ""}${value.toFixed(2)}`;
}

function proteinRule(kind: AppSnapshot["goals"]["kind"]) {
  if (kind === "gain") return "1.7 g/kg of trend weight";
  if (kind === "lose") return "2.0 g/kg of trend weight";
  return "1.8 g/kg of trend weight";
}

function calorieFloorForProtein(
  proteinGrams: number,
  fatShareOfRemainder: number,
  policy: AppSnapshot["checkIn"]["dailyFloorPolicy"],
) {
  const proteinCalories = Math.max(0, proteinGrams) * 4;
  const fatShare = Math.max(Number.EPSILON, fatShareOfRemainder);
  return Math.ceil(Math.max(
    policy.absoluteMinimumCalories,
    proteinCalories + policy.minimumNonProteinCalories,
    proteinCalories + (policy.minimumFatGrams * 9) / fatShare,
  ));
}
